import { useState, Suspense, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, X, RotateCcw, Pencil, Info, Zap } from 'lucide-react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Button } from '@lecartte/shared-ui/components/Button';
import { FormInput } from '@lecartte/shared-ui/components/FormInput';
import { LecartteSelect } from '@lecartte/shared-ui/components/fields/LecartteSelect';
import { Badge } from '@lecartte/shared-ui/components/Badge';
import { RoundIconButton } from '@lecartte/shared-ui/components/RoundIconButton';
import type { UsersPageQuery } from '@/__generated__/UsersPageQuery.graphql';
import type { UsersPageCapabilitiesQuery } from '@/__generated__/UsersPageCapabilitiesQuery.graphql';
import {
  canManageCapabilities,
  fetchRestaurantIdByEmail,
  graphqlFetch,
} from '@/lib/capabilities';

const QUERY = graphql`
  query UsersPageQuery($first: Int, $search: String, $role: String) {
    adminUsers(first: $first, search: $search, role: $role) {
      edges {
        node {
          id
          name
          email
          role
          isApproved
          rejectedAt
        }
      }
    }
  }
`;

const CAPABILITIES_QUERY = graphql`
  query UsersPageCapabilitiesQuery($email: String) {
    restaurantCapabilities(email: $email) {
      slug
      name
      type
      defaultValue
      planValue
      overrideValue
      overrideReason
      resolvedValue
      source
      plan
    }
    adminCapabilities {
      id
      slug
    }
  }
`;

const ROLE_LABELS: Record<string, string> = {
  CLIENT: 'Cliente',
  RESTAURANT: 'Restaurante',
  ADMIN: 'Admin',
};

const ROLE_VARIANTS: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  CLIENT: 'neutral',
  RESTAURANT: 'info',
  ADMIN: 'success',
};

function accountStatus({ isApproved, rejectedAt }: { isApproved: boolean; rejectedAt: string | null }): { label: string; variant: 'success' | 'warning' | 'danger' | 'info' | 'neutral' } {
  if (rejectedAt) return { label: 'Rejeitado', variant: 'danger' };
  if (isApproved) return { label: 'Aprovado', variant: 'success' };
  return { label: 'Em revisão', variant: 'warning' };
}



type CapRow = {
  readonly slug: string;
  readonly name: string;
  readonly type: string;
  readonly defaultValue: string;
  readonly planValue: string | null | undefined;
  readonly overrideValue: string | null | undefined;
  readonly overrideReason: string | null | undefined;
  readonly resolvedValue: string;
  readonly source: string;
  readonly plan: string | null | undefined;
};


function sourceLabel(source: string): string {
  switch (source) {
    case 'override':      return 'Alterado';
    case 'plan':          return 'Plano';
    case 'default':       return 'Padrão';
    case 'flag_disabled': return 'Flag Desativada';
    default:              return source;
  }
}

function sourceVariant(source: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  switch (source) {
    case 'override': return 'info';
    case 'plan': return 'info';
    case 'flag_disabled': return 'danger';
    default: return 'neutral';
  }
}

function resolvedDisplay(type: string, value: string) {
  if (type === 'boolean') {
    return value === 'true'
      ? <span className="text-green-600 font-semibold text-xs">✓ Ativo</span>
      : <span className="text-red-500 font-semibold text-xs">✗ Inativo</span>;
  }
  return <span className="font-mono font-semibold text-slate-800 text-xs">{value === '-1' ? '∞' : value}</span>;
}

function ReasonBadge({ reason }: { reason: string }) {
  return (
    <div className="group relative inline-flex">
      <span className="inline-flex items-center justify-center text-purple-500 cursor-default">
        <Info size={15} />
      </span>
      <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50 pointer-events-none w-max max-w-[220px]">
        <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl leading-relaxed">
          {reason}
        </div>
        <div className="w-2 h-2 bg-slate-900 rotate-45 ml-1 -mt-1" />
      </div>
    </div>
  );
}

function EditCapabilityModal({
  cap,
  capId,
  email,
  onClose,
  onRefresh,
}: {
  cap: CapRow;
  capId: string;
  email: string;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [inputVal, setInputVal] = useState(cap.overrideValue ?? cap.resolvedValue);
  const [reason, setReason] = useState(cap.overrideReason ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    setBusy(true);
    setError(null);
    const restaurantId = await fetchRestaurantIdByEmail({ email });
    if (!restaurantId) { setError('Restaurante não encontrado'); setBusy(false); return; }
    const json = await graphqlFetch({
      query: `mutation SetOverride($input: SetUserCapabilityOverrideInput!) {
        SetUserCapabilityOverride(input: $input) { override { value } error }
      }`,
      variables: {
        input: {
          restaurantId,
          capabilityId: capId,
          value: inputVal,
          ...(reason ? { reason } : {}),
        },
      },
    });
    const err = json?.data?.SetUserCapabilityOverride?.error;
    if (err) { setError(err); setBusy(false); return; }
    setBusy(false);
    onRefresh();
    onClose();
  }, [email, capId, inputVal, reason, onRefresh, onClose]);

  const handleRemove = useCallback(async () => {
    setBusy(true);
    setError(null);
    const restaurantId = await fetchRestaurantIdByEmail({ email });
    if (!restaurantId) { setError('Restaurante não encontrado'); setBusy(false); return; }
    await graphqlFetch({
      query: `mutation RemoveOverride($input: RemoveUserCapabilityOverrideInput!) {
        RemoveUserCapabilityOverride(input: $input) { success }
      }`,
      variables: { input: { restaurantId, capabilityId: capId } },
    });
    setBusy(false);
    onRefresh();
    onClose();
  }, [email, capId, onRefresh, onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-sm"
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <p className="font-mono text-xs font-bold text-slate-700 tracking-tight">{cap.slug}</p>
            <p className="text-xs text-slate-400 mt-0.5">{cap.name}</p>
          </div>
          <RoundIconButton onClick={onClose} className="mt-0.5" aria-label="Fechar">
            <X size={16} />
          </RoundIconButton>
        </div>

        <div className="px-5 py-4 flex flex-col gap-4">
          {/* Valor atual */}
          <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
            <span className="text-xs text-slate-500">Valor atual</span>
            <span className="font-semibold text-xs">{resolvedDisplay(cap.type, cap.resolvedValue)}</span>
          </div>

          {/* Novo valor */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="capability-override-value" className="text-xs font-medium text-slate-600">Novo valor</label>
            {cap.type === 'boolean' ? (
              <LecartteSelect
                id="capability-override-value"
                size="sm"
                value={inputVal}
                onValueChange={({ value }) => setInputVal(value)}
                options={[
                  { value: 'true', label: 'Ativo' },
                  { value: 'false', label: 'Inativo' },
                ]}
              />
            ) : (
              <FormInput
                type="number"
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                placeholder="-1 = ilimitado"
                className="text-xs w-28 py-1 px-2"
              />
            )}
          </div>

          {/* Motivo */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-slate-600">Motivo <span className="font-normal text-slate-400">(opcional)</span></label>
            <FormInput
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: teste de funcionalidade"
              className="text-xs py-1 px-2"
            />
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              disabled={busy}
              onClick={handleSave}
              className="flex-1"
            >
              {busy ? 'Salvando...' : 'Salvar'}
            </Button>
            {cap.overrideValue != null && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={handleRemove}
                title="Restaurar padrão"
                className="px-3"
              >
                <RotateCcw size={15} />
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function OverrideCell({
  cap,
  capId,
  email,
  onRefresh,
}: {
  cap: CapRow;
  capId: string;
  email: string;
  onRefresh: () => void;
}) {
  const [editing, setEditing] = useState(false);

  return (
    <>
      <div className="flex items-center gap-2">
        {cap.overrideReason && <ReasonBadge reason={cap.overrideReason} />}
        <Button size="sm" onClick={() => setEditing(true)}>
          <Pencil size={11} />
          {cap.overrideValue != null ? 'Editar' : 'Mudar'}
        </Button>
      </div>

      {editing && (
        <EditCapabilityModal
          cap={cap}
          capId={capId}
          email={email}
          onClose={() => setEditing(false)}
          onRefresh={onRefresh}
        />
      )}
    </>
  );
}

function CapabilitiesModalContent({
  email,
  fetchKey,
  onRefresh,
}: {
  email: string;
  fetchKey: number;
  onRefresh: () => void;
}) {
  const data = useLazyLoadQuery<UsersPageCapabilitiesQuery>(
    CAPABILITIES_QUERY,
    { email },
    { fetchKey, fetchPolicy: 'network-only' }
  );

  const caps = data.restaurantCapabilities;
  const capIndex = Object.fromEntries(data.adminCapabilities.map((c) => [c.slug, c.id]));

  if (!caps || caps.length === 0) {
    return (
      <p className="text-sm text-slate-400 text-center py-6">
        Nenhum restaurante encontrado para este e-mail.
      </p>
    );
  }

  const planName = caps[0]?.plan ?? null;
  const hasOverrides = caps.some((c) => c.source === 'override');
  const [resetting, setResetting] = useState(false);

  const handleResetAll = useCallback(async () => {
    if (!confirm('Resetar todas as capacidades para o padrão do plano? Todos os overrides serão removidos.')) return;
    setResetting(true);
    const restaurantId = await fetchRestaurantIdByEmail({ email });
    if (!restaurantId) { setResetting(false); return; }
    await graphqlFetch({
      query: `mutation ResetAllOverrides($input: ResetAllCapabilityOverridesInput!) {
        ResetAllCapabilityOverrides(input: $input) { removedCount error }
      }`,
      variables: { input: { restaurantId } },
    });
    setResetting(false);
    onRefresh();
  }, [email, onRefresh]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {planName && (
            <>
              <span className="text-sm text-slate-500">Plano:</span>
              <Badge label={planName} variant="info" />
            </>
          )}
        </div>
        {hasOverrides && (
          <Button
            variant="outline"
            size="sm"
            disabled={resetting}
            onClick={handleResetAll}
            title="Restaurar padrão do plano"
          >
            <RotateCcw size={12} />
            {resetting ? 'Resetando...' : 'Resetar para padrão do plano'}
          </Button>
        )}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Capability</th>
              <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Padrão</th>
              <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Plano</th>
              <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Valor</th>
              <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Origem</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Ação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {caps.map((cap) => {
              const src = sourceLabel(cap.source);
              return (
                <tr key={cap.slug} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-4 py-2.5">
                    <span className="font-mono text-xs font-semibold text-slate-800 tracking-tight">{cap.slug}</span>
                    <p className="text-xs text-slate-400 mt-0.5">{cap.name}</p>
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs text-slate-400 font-mono">
                    {cap.type === 'boolean'
                      ? (cap.defaultValue === 'true' ? '✓' : '✗')
                      : (cap.defaultValue === '-1' ? '∞' : cap.defaultValue)}
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs font-mono">
                    {cap.planValue != null
                      ? (cap.type === 'boolean'
                          ? (cap.planValue === 'true'
                              ? <span className="text-green-600">✓</span>
                              : <span className="text-red-400">✗</span>)
                          : <span className="text-slate-600">{cap.planValue === '-1' ? '∞' : cap.planValue}</span>)
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {resolvedDisplay(cap.type, cap.resolvedValue)}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Badge label={src} variant={sourceVariant(cap.source)} />
                  </td>
                  <td className="px-4 py-2.5">
                    <OverrideCell
                      cap={cap}
                      capId={capIndex[cap.slug] ?? ''}
                      email={email}
                      onRefresh={onRefresh}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CapabilitiesModal({
  email,
  name,
  onClose,
}: {
  email: string;
  name: string;
  onClose: () => void;
}) {
  const [fetchKey, setFetchKey] = useState(0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, y: 32, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="font-display font-bold text-slate-900 text-lg">Capacidades</h2>
            <p className="text-sm text-slate-500">{name} · {email}</p>
          </div>
          <RoundIconButton onClick={onClose} aria-label="Fechar">
            <X size={16} />
          </RoundIconButton>
        </div>
        <div className="overflow-y-auto flex-1 p-6">
          <Suspense
            fallback={
              <p className="text-sm text-slate-400 text-center py-10">Carregando capacidades...</p>
            }
          >
            <CapabilitiesModalContent
              email={email}
              fetchKey={fetchKey}
              onRefresh={() => setFetchKey((k) => k + 1)}
            />
          </Suspense>
        </div>
      </motion.div>
    </div>
  );
}

function UsersTable({
  search,
  role,
}: {
  search: string;
  role: string;
}) {
  const [capModal, setCapModal] = useState<{ email: string; name: string } | null>(null);

  const data = useLazyLoadQuery<UsersPageQuery>(QUERY, {
    first: 50,
    search: search || null,
    role: role || null,
  });

  const users = data.adminUsers.edges.flatMap((e) => e?.node ? [e.node] : []);

  if (users.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center text-slate-400 text-sm">
        Nenhum usuário encontrado.
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Nome</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Role</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Capacidades</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-900">{user.name}</td>
                <td className="px-4 py-3 text-slate-500">{user.email}</td>
                <td className="px-4 py-3">
                  <Badge label={ROLE_LABELS[user.role] ?? user.role} variant={ROLE_VARIANTS[user.role] ?? 'neutral'} />
                </td>
                <td className="px-4 py-3">
                  {(() => {
                    const status = accountStatus({ isApproved: user.isApproved, rejectedAt: user.rejectedAt ?? null });
                    return <Badge label={status.label} variant={status.variant} />;
                  })()}
                </td>
                <td className="px-4 py-3">
                  {canManageCapabilities(user.role) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCapModal({ email: user.email, name: user.name })}
                    >
                      <Zap size={12} />
                      Ver capacidades
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {capModal && (
        <CapabilitiesModal
          email={capModal.email}
          name={capModal.name}
          onClose={() => setCapModal(null)}
        />
      )}
    </>
  );
}

const ROLE_FILTER_ALL = 'ALL';

const ROLE_FILTER_OPTIONS = [
  { value: ROLE_FILTER_ALL, label: 'Todos os roles' },
  { value: 'CLIENT', label: 'Cliente' },
  { value: 'RESTAURANT', label: 'Restaurante' },
  { value: 'ADMIN', label: 'Admin' },
];

export function UsersPage() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
          <FormInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou email..."
            className="pl-9 py-2"
          />
        </div>
        <LecartteSelect
          ariaLabel="Filtrar por papel"
          className="w-52 shrink-0"
          value={roleFilter || ROLE_FILTER_ALL}
          onValueChange={({ value }) => setRoleFilter(value === ROLE_FILTER_ALL ? '' : value)}
          options={ROLE_FILTER_OPTIONS}
        />
      </div>

      <Suspense
        fallback={
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center text-slate-400 text-sm">
            Carregando...
          </div>
        }
      >
        <UsersTable search={search} role={roleFilter} />
      </Suspense>
    </div>
  );
}
